<template>
  <div
    class="relative rounded-xl overflow-hidden select-none"
    :style="{
      height: '30px',
      marginBottom: flush ? '0' : '6px',
      background: bgColor,
      borderBottomLeftRadius: flush ? '0' : undefined,
      borderBottomRightRadius: flush ? '0' : undefined,
      // Синхронно с ExpandTransition блока детализации: углы и отступ
      // «срастаются» с раскрывающимся блоком, а не щёлкают мгновенно
      transition: 'margin-bottom .28s cubic-bezier(0.4, 0, 0.2, 1), border-bottom-left-radius .28s cubic-bezier(0.4, 0, 0.2, 1), border-bottom-right-radius .28s cubic-bezier(0.4, 0, 0.2, 1)',
    }"
  >
    <!-- Цветная заливка -->
    <div
      v-if="!noNorm"
      class="absolute left-0 top-0 bottom-0 rounded-xl transition-all duration-500"
      :style="{ width: fillPct + '%', backgroundColor: color }"
    />

    <!-- Вертикальная метка верхнего предела (на 70% ширины) -->
    <div
      v-if="!noNorm"
      class="absolute top-0 bottom-0"
      :style="{ left: '70%', width: '1.5px', background: 'rgba(65,41,26,0.25)' }"
    />

    <!-- Вертикальная метка нижнего предела (если задан) -->
    <div
      v-if="lower != null"
      class="absolute top-0 bottom-0"
      :style="{ left: lowerPct + '%', width: '1.5px', background: 'rgba(65,41,26,0.25)' }"
    />

    <!-- Подпись к нижней метке (если задана) -->
    <span
      v-if="lower != null && lowerLabel"
      class="absolute z-[1]"
      :style="{
        left: lowerPct + '%',
        top: '50%',
        transform: 'translate(calc(-100% - 3px), -50%)',
        fontFamily: 'Inter, sans-serif',
        fontSize: '12px',
        fontWeight: 400,
        color: 'rgba(65,41,26,0.6)',
        whiteSpace: 'nowrap',
      }"
    >{{ lowerLabel }}</span>

    <!-- Название нутриента + норма — слева -->
    <div
      class="absolute flex items-baseline z-[1]"
      style="left: 8px; top: 50%; transform: translateY(-50%); gap: 8px;"
    >
      <span
        class="leading-none text-ink"
        style="font-family: Inter, sans-serif; font-size: var(--fs-14); font-weight: 600;"
      >{{ label }}</span>
      <span
        v-if="!noNorm || normText"
        class="leading-none whitespace-nowrap"
        style="font-family: Inter, sans-serif; font-size: var(--fs-12); font-weight: 400; color: rgba(65,41,26,0.75);"
      ><span v-if="!normText" class="approx-mark">~</span>{{ normText || normLabel }}</span>
    </div>

    <!-- Значение — по центру вертикали, справа -->
    <span
      v-if="loading"
      class="absolute flex items-center gap-[3px] z-[1]"
      style="right: 8px; top: 50%; transform: translateY(-50%);"
      aria-label="Считаем"
    >
      <i v-for="n in 3" :key="n" class="macro-dot" :style="{ animationDelay: (n - 1) * 0.16 + 's', background: color }" />
    </span>
    <span
      v-else
      class="absolute leading-none z-[1]"
      :style="{
        fontFamily: 'Inter, sans-serif',
        fontSize: noData ? '12px' : '16px',
        fontWeight: noData ? 400 : 600,
        color: noData ? 'rgba(65,41,26,0.75)' : '#41291A',
        right: '8px', top: '50%', transform: 'translateY(-50%)',
      }"
    >
      <!-- ~ ставится, когда часть значения — оценка ИИ, а не измерение.
           Без него догадка выглядит как точная цифра. -->
      <span v-if="approx && !noData" class="approx-mark">~</span>{{ displayValue }}
    </span>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  label:    { type: String,  required: true },
  value:    { type: Number,  default: 0 },
  unit:     { type: String,  default: '' },
  upper:    { type: Number,  required: true },
  lower:      { type: Number,  default: null },
  lowerLabel: { type: String,  default: null },
  color:      { type: String,  default: '#4CAF50' },
  noNorm:   { type: Boolean, default: false },
  normText: { type: String,  default: null },
  precise:  { type: Boolean, default: false },
  noData:   { type: Boolean, default: false },
  // Значение ещё считается — вместо цифры показываем точки
  loading:  { type: Boolean, default: false },
  // В значение входит оценка ИИ, а не только измеренные данные
  approx:   { type: Boolean, default: false },
  // Снизу к полоске примыкает раскрытая панель: убираем отступ и скругление,
  // чтобы полоска и панель читались одной областью, а не двумя блоками.
  flush:    { type: Boolean, default: false },
})

const bgColor = computed(() => {
  if (props.noNorm) return props.color
  // 20% opacity version of the color
  return props.color + '33'
})

const maxScale = computed(() => props.upper / 0.7)

const lowerPct = computed(() =>
  props.lower != null ? (props.lower / maxScale.value) * 100 : null
)

const fillPct = computed(() =>
  Math.min((props.value / maxScale.value) * 100, 100)
)

const displayValue = computed(() => {
  if (props.noData) return 'нет данных'
  if (!props.precise) return Math.round(props.value)
  const fixed = props.value.toFixed(1)
  return fixed.endsWith('.0') ? Math.round(props.value) : fixed
})

// Знак приблизительности вынесен в шаблон отдельным span (.approx-mark):
// внутри строки его не подать заметнее, а читался как минус именно он.
const normLabel = computed(() => {
  const range = (props.lower != null && !props.lowerLabel) ? `${props.lower}–${props.upper}` : `${props.upper}`
  return `${range} ${props.unit}`
})
</script>

<style scoped>
.macro-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  display: inline-block;
  opacity: 0.35;
  animation: macro-dot-pulse 1s ease-in-out infinite;
}
@keyframes macro-dot-pulse {
  0%, 100% { opacity: 0.25; transform: scale(0.8); }
  50%      { opacity: 0.9;  transform: scale(1); }
}
@media (prefers-reduced-motion: reduce) {
  .macro-dot { animation: none; opacity: 0.5; }
}
</style>
